import React, { useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LifeBuoy, Save, Loader2, Info } from "lucide-react";

const C = { navy: "#1C4358", slate: "#4B5563", charcoal: "#252221", line: "#E6EDF2", amber: "#B7791F" };

const Row = ({ title, desc, children }) => (
  <div className="flex items-start justify-between gap-4 py-3 border-b last:border-0" style={{ borderColor: C.line }}>
    <div className="min-w-0"><div className="text-[13.5px] font-medium" style={{ color: C.charcoal }}>{title}</div>
      <div className="text-[12px]" style={{ color: C.slate }}>{desc}</div></div>
    <div className="shrink-0">{children}</div>
  </div>
);

export default function HelpAssistantSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/help-assistant/settings").then(({ data }) => setS(data)).catch((e) => toast.error(errMsg(e))); }, []);
  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/help-assistant/settings", {
        assistant_enabled: s.assistant_enabled, ai_suggestions_enabled: s.ai_suggestions_enabled,
        contextual_help_enabled: s.contextual_help_enabled, review_required: s.review_required,
        tone: s.tone, ai_disclaimer: s.ai_disclaimer,
      });
      setS((x) => ({ ...x, ...data })); toast.success("Settings saved.");
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  if (!s) return <div className="max-w-2xl mx-auto space-y-3"><Skeleton className="h-10 w-1/2" /><Skeleton className="h-64" /></div>;
  const disabled = !s.can_edit;

  return (
    <div className="max-w-2xl mx-auto pb-16" data-testid="help-settings-page">
      <h1 className="font-display text-2xl font-bold flex items-center gap-2 mb-1" style={{ color: C.charcoal }}>
        <LifeBuoy size={22} style={{ color: C.navy }} /> Help Assistant Settings
      </h1>
      <p className="text-[13px] mb-4" style={{ color: C.slate }}>Control the backend help assistant, contextual page help, and AI SOP suggestions.</p>

      {!s.ai_configured && (
        <div className="rounded-lg p-3 mb-4 text-[12.5px] flex items-start gap-2" style={{ background: "#FEF3C7", color: "#92400E" }} data-testid="help-ai-warning">
          <Info size={15} className="mt-0.5 shrink-0" /> AI is not configured — the assistant falls back to guide-based answers. The SOP Library and contextual help still work.
        </div>
      )}
      {disabled && <div className="rounded-lg p-3 mb-4 text-[12.5px]" style={{ background: "#EEF2F5", color: C.slate }}>Only a Super Admin can change these settings.</div>}

      <Card className="p-5 border-[#E6EDF2]">
        <Row title="Backend help assistant" desc="Allow staff to ask the assistant questions.">
          <Switch data-testid="set-assistant-enabled" disabled={disabled} checked={s.assistant_enabled} onCheckedChange={(v) => set("assistant_enabled", v)} />
        </Row>
        <Row title="Contextual page help" desc="Show the 'Help with this page' drawer across screens.">
          <Switch data-testid="set-contextual-enabled" disabled={disabled} checked={s.contextual_help_enabled} onCheckedChange={(v) => set("contextual_help_enabled", v)} />
        </Row>
        <Row title="AI SOP suggestions" desc="Let the system suggest new SOP drafts from repeated questions (admin approval required).">
          <Switch data-testid="set-suggestions-enabled" disabled={disabled} checked={s.ai_suggestions_enabled} onCheckedChange={(v) => set("ai_suggestions_enabled", v)} />
        </Row>
        <Row title="Require SOP review" desc="New/edited SOPs must be reviewed before publishing.">
          <Switch data-testid="set-review-required" disabled={disabled} checked={s.review_required} onCheckedChange={(v) => set("review_required", v)} />
        </Row>
        <Row title="Assistant tone" desc="Default answering style.">
          <Select value={s.tone} onValueChange={(v) => set("tone", v)} disabled={disabled}>
            <SelectTrigger className="h-8 w-[180px] text-[13px]" data-testid="set-tone"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="warm">Warm professional</SelectItem>
              <SelectItem value="concise">Concise</SelectItem>
              <SelectItem value="detailed">Detailed</SelectItem>
            </SelectContent>
          </Select>
        </Row>
        <div className="pt-3">
          <Label className="text-[12.5px]" style={{ color: C.slate }}>AI disclaimer text</Label>
          <Textarea data-testid="set-disclaimer" disabled={disabled} value={s.ai_disclaimer} onChange={(e) => set("ai_disclaimer", e.target.value)} rows={2} className="mt-1 text-[13px]" />
        </div>
      </Card>

      {!disabled && (
        <div className="flex justify-end mt-4">
          <Button onClick={save} disabled={saving} data-testid="help-settings-save" className="h-9 gap-1 bg-[#1C4358] hover:bg-[#16374a] text-white">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save settings
          </Button>
        </div>
      )}
    </div>
  );
}
